import get from "lodash.get";
import {
  Control,
  Controller,
  FieldErrors,
  FieldPath,
  FieldValues,
  RegisterOptions,
} from "react-hook-form";

import { UsersFilterableSinglePicker } from "src/components/Pickers/User/UserFilterableSinglePicker";
import { useT } from "src/internationalization";

export type UsersFilterableSinglePickerFormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  id?: string;
  label: string;
  helperText?: string;
  placeholder?: string;
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  name: TName;
};

export function UsersFilterableSinglePickerFormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  id,
  errors,
  name,
  label,
  placeholder,
  control,
  helperText,
  rules,
}: UsersFilterableSinglePickerFormFieldProps<TFieldValues, TName>) {
  const error = get(errors, name);
  const t = useT();

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { onChange, value } }) => (
        <UsersFilterableSinglePicker
          id={id}
          label={label ?? t("user.picker.title.singular")}
          selectedUser={value}
          onChange={onChange}
          invalidText={typeof error?.message === "string" ? error.message : undefined}
          invalid={!!error?.message}
          helperText={helperText}
          placeholder={placeholder ?? t("user.picker.placeholder")}
        />
      )}
    />
  );
}
